import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * ============================================================================
 * DEV-ONLY AUTH STUB -- NOT PRODUCTION AUTHENTICATION
 * ============================================================================
 *
 * console now has real authentication (see stores/auth.ts, lib/api/auth.ts,
 * routes/login.tsx, routes/register.tsx). This store is a SECONDARY,
 * opt-in-only escape hatch (see DevAuthPanel.tsx's VITE_ENABLE_DEV_AUTH_PANEL
 * gate and "no real user signed in" condition) retained for local
 * development, by letting a developer manually paste an organization_id
 * (and optionally a user_id to "act as") into local browser state without
 * going through a full register/login flow -- most useful when running
 * console-server without JWT_SECRET set (an intentionally supported
 * local-dev mode).
 *
 * This is NOT a login form. It does not authenticate anyone. It does not
 * talk to any backend endpoint. It is a plain localStorage-backed value
 * that other parts of the app read to know "which org am I looking at" in
 * lieu of a real session. Every surface for this (the store, the panel
 * component, this comment) is named so it cannot be mistaken for
 * production auth, and it never renders once a real session exists (see
 * DevAuthPanel.tsx).
 */

interface DevAuthState {
  organizationId: string;
  userId: string;
  setOrganizationId: (organizationId: string) => void;
  setUserId: (userId: string) => void;
  clear: () => void;
}

export const useDevAuthStore = create<DevAuthState>()(
  persist(
    (set) => ({
      organizationId: "",
      userId: "",
      setOrganizationId: (organizationId) => set({ organizationId }),
      setUserId: (userId) => set({ userId }),
      clear: () => set({ organizationId: "", userId: "" }),
    }),
    {
      // Deliberately distinct key so it is obvious in devtools/localStorage
      // inspection that this is a dev-only artifact, never a real session.
      name: "console-web:DEV_AUTH_DO_NOT_USE_IN_PRODUCTION",
    },
  ),
);
